/**
 * Smoothing and noise filtering algorithms
 */

export interface SmoothingConfig {
  alpha: number;             // EMA factor: 0.0 (maximum smoothing) to 1.0 (instant, raw)
  maxRateOfChangeKmhPerSec: number; // Maximum realistic acceleration spike allowed (e.g. 35 km/h per sec)
  stationaryThresholdKmh: number;   // Below this speed, force to 0 to prevent GPS wander
}

export const DEFAULT_SMOOTHING_CONFIG: SmoothingConfig = {
  alpha: 0.35,
  maxRateOfChangeKmhPerSec: 35.0, // ~10 m/s^2 (supercar level acceleration clamp)
  stationaryThresholdKmh: 1.5,
};

/**
 * Exponential Moving Average (EMA)
 */
export function exponentialMovingAverage(
  current: number,
  previous: number,
  alpha: number
): number {
  const clampedAlpha = Math.max(0.01, Math.min(1.0, alpha));
  return previous * (1 - clampedAlpha) + current * clampedAlpha;
}

/**
 * Clamp speed change to physically realistic vehicle bounds
 */
export function clampRateOfChange(
  current: number,
  previous: number,
  dtSeconds: number,
  maxRateKmhPerSec: number
): number {
  if (dtSeconds <= 0) return previous;
  const maxDelta = maxRateKmhPerSec * dtSeconds;
  const delta = current - previous;

  if (delta > maxDelta) {
    return previous + maxDelta;
  }
  // Allow faster braking deceleration (up to 2x acceleration limit)
  if (delta < -maxDelta * 2) {
    return previous - maxDelta * 2;
  }

  return current;
}
